import { useEffect, useCallback } from 'react';

// GA4 Measurement ID - will be set via environment or config
const GA4_MEASUREMENT_ID = process.env.REACT_APP_GA4_ID || '';

// Check if user has consented to analytics cookies
const hasAnalyticsConsent = () => {
  try {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) return false;
    const parsed = JSON.parse(consent);
    return parsed.analytics === true;
  } catch {
    return false;
  }
};

// Initialize GA4
const initGA4 = () => {
  if (!GA4_MEASUREMENT_ID || typeof window === 'undefined') return;
  
  // Don't initialize if already done
  if (window.gtag) return;
  
  // Load gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);
  
  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  
  window.gtag('js', new Date());
  window.gtag('config', GA4_MEASUREMENT_ID, {
    send_page_view: true,
    cookie_flags: 'SameSite=None;Secure'
  });
};

// Track page view
export const trackPageView = (path, title) => {
  if (!hasAnalyticsConsent() || !window.gtag) return;
  
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title
  });
};

// Track custom event
export const trackEvent = (eventName, params = {}) => {
  if (!hasAnalyticsConsent() || !window.gtag) return;
  
  window.gtag('event', eventName, params);
};

// Specific event trackers for booking intent
export const trackBookACallClick = (source) => {
  trackEvent('book_a_call_click', {
    event_category: 'conversion',
    event_label: source,
    value: 1
  });
};

export const trackOutboundBookingClick = (destination, utmParams) => {
  trackEvent('outbound_click_to_booking', {
    event_category: 'conversion',
    event_label: destination,
    utm_source: utmParams?.utm_source || 'website',
    utm_medium: utmParams?.utm_medium || 'referral',
    utm_campaign: utmParams?.utm_campaign || 'treatyourbusiness'
  });
};

// Track content engagement
export const trackContentEngagement = (contentType, contentId, action) => {
  trackEvent('content_engagement', {
    content_type: contentType, // 'podcast', 'video', 'blog', 'short'
    content_id: contentId,
    action: action // 'play', 'pause', 'complete', 'share'
  });
};

// GA4 Provider Component
export const Analytics = () => {
  useEffect(() => {
    // Only initialize if consent given
    if (hasAnalyticsConsent()) {
      initGA4();
    }
    
    // Listen for consent changes
    const handleConsentChange = () => {
      if (hasAnalyticsConsent()) {
        initGA4();
      }
    };
    
    window.addEventListener('cookie_consent_updated', handleConsentChange);
    
    return () => {
      window.removeEventListener('cookie_consent_updated', handleConsentChange);
    };
  }, []);
  
  return null;
};

// Hook for tracking page views on route change
export const usePageTracking = () => {
  useEffect(() => {
    // Track initial page view
    if (hasAnalyticsConsent()) {
      trackPageView(window.location.pathname, document.title);
    }
  }, []);
};

// UTM parameter builder
export const buildBookingUrl = (baseUrl, source) => {
  const utmParams = new URLSearchParams({
    utm_source: 'website',
    utm_medium: 'referral',
    utm_campaign: 'treatyourbusiness',
    utm_content: source
  });
  
  return `${baseUrl}?${utmParams.toString()}`;
};

export default {
  Analytics,
  trackPageView,
  trackEvent,
  trackBookACallClick,
  trackOutboundBookingClick,
  trackContentEngagement,
  usePageTracking,
  buildBookingUrl
};
